import { Form, Formik } from 'formik';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { Button, Divider, Grid, Header, Icon, Message, Popup } from 'semantic-ui-react';
import MyTextInput from '../../app/common/form/MyTextInput';
import { useStore } from '../../app/stores/store';
import * as Yup from 'yup';
import MyCheckbox from '../../app/common/form/MyCheckbox';
import { history } from "../..";
import SignatureCanvas from 'react-signature-canvas';


export default observer(function AgreementForm() {
    const {residentStore, modalStore} = useStore();
    const {selectedResident: resident} = residentStore;
   
    const canvasRef= useRef<SignatureCanvas>(null);
    const canvasDivRef = useRef<HTMLDivElement>(null);
    const initialCanvasWidth = 300;

    // Info messages
    const [showFirstNameInfo, setShowFirstNameInfo] = useState('');
    const [showSurnameInfo, setShowSurnameInfo] = useState('');
    const [showEmployeeNumberInfo, setShowEmployeeNumberInfo] = useState('');

    // REGEX for validatons
    const mobileRegex = /^04\d{8}$/;
    const phoneRegex = /^\d{8,11}$/;
    const employeeNumberRegex = /^\d+$|^[Nn][Aa]$/;
    const fullNameRegex = /^.+\s.+$/;

    // Handle Canvas Draw onChange - const below update the signaturePoints so we know there is a signature (which is required)
    const canvasDrawOnChange = (setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void) => {
        if (canvasRef?.current) {                      
            var point = canvasRef.current.isEmpty() ? 0 : 1;
            setFieldValue('signaturePoints', point);
        }
    }    

    useEffect(() => {  
        const canvasDiv = canvasDivRef?.current;  
        if (canvasDiv != null && canvasRef.current?.getCanvas().width === initialCanvasWidth)
        {           
            let updatedWidth = canvasDiv.offsetWidth - 10;
            console.log('useEffect updated width to ' + updatedWidth); 
            canvasRef.current.getCanvas().setAttribute('width', updatedWidth.toString());
            canvasRef.current.clear();
        }
    }, [canvasDivRef?.current]);


    return (
        <Formik 
            initialValues={{sid: resident?.sid, 
                fullName: resident?.fullName ? resident?.fullName : '', 
                firstName: resident?.firstName ? resident?.firstName : '', 
                surname: resident?.surname ? resident?.surname : '', 
                email: resident?.email ? resident?.email : '', 
                mobileNumber: resident?.mobileNumber ? resident?.mobileNumber : '', 
                department: resident?.department ? resident?.department : '',
                supervisorName: resident?.supervisorName ? resident?.supervisorName : '',
                supervisorMobile: resident?.supervisorMobile ? resident?.supervisorMobile : '',
                employeeNumber: resident?.employeeNumber ? resident?.employeeNumber : '', 
                signaturePoints: 0,                
                readAndAgreedWithRules: resident?.readAndAgreedWithRules, 
                hasIncorrectPersonalDetails : resident?.hasIncorrectPersonalDetails,
                ruleVersion: resident?.ruleVersion,
                error: null}}
                
            onSubmit={(values) => {    
                       residentStore.updateResident(values, canvasRef?.current)
                            .catch(error => {
                                console.log(error.response?.data);
                                modalStore.closeModal();
                                history.push('/server-error');
                            })                                         
                 }
            }
            
            validationSchema={
                (resident?.publicUser && Yup.object({
                    // fullName: Yup.string().required('Name is required').min(3, 'Name should be at least 3 characters'),
                    firstName: Yup.string().required('First name is required'),
                    surname: Yup.string().required('Surname is required'),
                    // 20240110 - AK - requested by NiW to make Employee/ERMS Number non-mandatory
                    //employeeNumber: Yup.string().required('Employee/ERMS No. is required')
                    //                            .matches(employeeNumberRegex, {message: 'Enter numbers only, or NA if unkown or if you are a visitor', excludeEmptyString: true}),
                    email: Yup.string().required('Email is required').email('Email is not valid'),
                    mobileNumber: Yup.string().required('Mobile number is required').matches(mobileRegex, {message: 'Mobile must start with 04 and has 10 numbers', excludeEmptyString: true}),
                    department: Yup.string().required('Department/Company is required'),
                    supervisorName: Yup.string().required('Supervisor name is required').matches(fullNameRegex, {message: 'Full name must have first and last names', excludeEmptyString: true}),
                    supervisorMobile: Yup.string().required('Supervisor number is required').matches(mobileRegex, {message: 'Mobile must start with 04 and has 10 numbers', excludeEmptyString: true}),
                    readAndAgreedWithRules: Yup.boolean().oneOf([true], 'You must agree to the rules to proceed'),
                    signaturePoints: Yup.number().positive('Signature is required')
                    })) ||
                (!resident?.publicUser && Yup.object({     
                    // 20240110 - AK - requested by NiW to  make Employee/ERMS Number non-mandatory
                    //employeeNumber: Yup.string().required('Employee/ERMS No. is required')
                    //                            .matches(employeeNumberRegex, {message: 'Enter numbers only, or NA if unkown or if you are a visitor', excludeEmptyString: true}),              
                    email: Yup.string().required('Email is required').email('Email is not valid'),
                    department: Yup.string().required('Department/Company is required'),
                    supervisorName: Yup.string().required('Supervisor name is required').matches(fullNameRegex, {message: 'Full name must have first and last names', excludeEmptyString: true}),
                    supervisorMobile: Yup.string().required('Supervisor number is required').matches(mobileRegex, {message: 'Mobile must start with 04 and has 10 numbers', excludeEmptyString: true}),
                    readAndAgreedWithRules: Yup.boolean().oneOf([true], 'You must agree to the rules to proceed'),
                    signaturePoints: Yup.number().positive('Signature is required')
                    }))  

            }
        >
            {({handleSubmit, isSubmitting, errors, setFieldValue, handleBlur}) => (
                <Form className='ui form error' onSubmit={handleSubmit} autoComplete='off'>
                    <Header as='h3' content='Resident Agreement' subheader={'Village Rules Version ' + resident?.ruleVersion}
                        className="entcolor"  textAlign='left' />
                    <Message icon size='tiny'>
                        <Icon name='privacy' size='tiny' />
                        <Message.Content >
                            Your data is being collected in accordance with <a href="https://www.bhp.com/info/privacy-policy/" target="_blank">BHP’s Privacy Policy</a>. 
                            For BHP employees, please refer to <a href="https://spo.bhpbilliton.com/sites/DW/Pages/Privacy.aspx" target="_blank">Global Privacy Notice for BHP Workers</a>. 
                        </Message.Content>
                    </Message>
                    <Divider/>
                    <Grid stackable >
                        {!resident?.publicUser &&
                            <Grid.Row columns={1}>                            
                                <Grid.Column>                                                                                 
                                    <MyTextInput name='fullName' disabledvalue={resident?.fullName} placeholder='' maxLength={250} 
                                        label='Resident Name' required  disabled={true} />                                   
                                </Grid.Column>                                                                 
                            </Grid.Row>   
                        }
                        {resident?.publicUser &&
                            <Grid.Row columns={2}>                            
                                <Grid.Column>                                                                                 
                                    <MyTextInput name='firstName' disabledvalue={resident?.firstName} placeholder='' maxLength={150} 
                                        label='Resident First Name' required infomessage={showFirstNameInfo}
                                        onFocus={() => setShowFirstNameInfo('Please enter the same first name as your access card')}
                                        onBlur={e => {                                           
                                            // call the built-in handleBur
                                            handleBlur(e);
                                            // and do something about e
                                            setShowFirstNameInfo('');                                          
                                        }}
                                        readOnly={isSubmitting} />                                   
                                </Grid.Column>
                                
                                <Grid.Column>                                                                                 
                                    <MyTextInput name='surname' disabledvalue={resident?.surname} placeholder='' maxLength={150} 
                                        label='Resident Surname' required infomessage={showSurnameInfo}
                                        onFocus={() => setShowSurnameInfo('Please enter the same surname as your access card')}
                                        onBlur={e => {
                                            // call the built-in handleBur
                                            handleBlur(e);
                                            // and do something about e
                                            setShowSurnameInfo('');
                                        }}
                                        readOnly={isSubmitting} />                                   
                                </Grid.Column>
                            </Grid.Row>                                                           
                        }
                        
                        <Grid.Row columns={2}>                            
                            <Grid.Column >
                                <MyTextInput name='email' placeholder='@' label='Resident Email' required type='email' maxLength={320}
                                    infomessage='Please enter a valid email. Agreement confirmation will be sent to this email.'
                                    readOnly={isSubmitting}
                                />                              
                            </Grid.Column>
                            <Grid.Column >
                                <MyTextInput name='mobileNumber' placeholder='AU Mobile Number only: 04...' label='Resident Mobile Number'  
                                    maxLength={10} type='tel'
                                    disabledvalue={resident?.mobileNumber}
                                    required  disabled={!resident?.publicUser} 
                                    readOnly={isSubmitting}/>                                
                            </Grid.Column>
                        </Grid.Row> 

                        
                        {/* 20240110 - AK - requested by NiW to make Employee/ERMS Number field non-mandatory as differed from other assets*/}
                        <Grid.Row columns={1}>                            
                            <Grid.Column >
                                <MyTextInput name='employeeNumber' placeholder='' label='Employee/ERMS Number' maxLength={50}  
                                        //infomessage={showEmployeeNumberInfo}                                                                                       
                                        //onFocus={() => setShowEmployeeNumberInfo('Please enter NA if unknown or if you are a visitor')}
                                        // onBlur={e => {
                                        //     // call the built-in handleBur
                                        //     handleBlur(e);
                                        //     // and do something about e
                                        //     setShowEmployeeNumberInfo('');
                                        // }} 
                                        readOnly={isSubmitting}
                                />                                
                            </Grid.Column>                                 
                        </Grid.Row>                                                           
                        
                        <Grid.Row columns={1}>
                            <Grid.Column >
                                <MyTextInput name='department' placeholder='' label='BHP Department/Contractor Company' maxLength={250}                                   
                                    required 
                                    readOnly={isSubmitting}/>
                            </Grid.Column>                             
                        </Grid.Row>
                                         
                        <Grid.Row columns={2}>                            
                            <Grid.Column >
                                <MyTextInput name='supervisorName' placeholder=''  maxLength={250}
                                    label="Supervisor Full Name" 
                                    required
                                    readOnly={isSubmitting}
                                    tooltip={resident?.publicUser ? undefined : 'If details are incorrect, please update'}/>    
                                                           
                            </Grid.Column>
                            <Grid.Column >
                                <MyTextInput name='supervisorMobile' 
                                    placeholder='AU Mobile Number only: 04...' maxLength={10}
                                    label="Supervisor Mobile Number" type='tel'
                                    required
                                    readOnly={isSubmitting}
                                    tooltip={resident?.publicUser ? undefined : 'If details are incorrect, please update'} />                                
                            </Grid.Column>
                        </Grid.Row> 
                                          
                        {/* {!resident?.publicUser &&                        
                            <Grid.Row columns={1}>                            
                                <Grid.Column >
                                    <MyCheckbox name='hasIncorrectPersonalDetails' 
                                        label='If any of your details are incorrect or incomplete, please tick here so you can be contacted.' 
                                        disabled={isSubmitting}/>                              
                                </Grid.Column>                               
                            </Grid.Row> 
                        }                                                                            */}
                    </Grid>
                                                                                             
                    <Divider/>
                        <div className='required field' ref={canvasDivRef} >
                            <label>Signature</label>  
                                <SignatureCanvas penColor='blue' clearOnResize={false} 
                                    canvasProps={{height: 250, width:initialCanvasWidth, className: 'sigCanvas'}}
                                    ref={canvasRef} onEnd={() => canvasDrawOnChange(setFieldValue)} />                        
                                <Button fluid content="Clear Signature" 
                                    onClick={() => {canvasRef?.current?.clear(); setFieldValue('signaturePoints', 0); return false;}} type='button'
                                    disabled={isSubmitting}/>   
                                <MyTextInput name='signaturePoints' placeholder='' type='hidden'/>                                          
                        </div>
                    <Divider/>

                    <MyCheckbox required name='readAndAgreedWithRules' 
                        label='I have read and understood and agree to these rules, as amended from time to time and notified to me. I understand that failure to abide may result in withdrawal of accommodation and/or disciplinary action.' 
                        disabled={isSubmitting}/> 

                    <Divider/>            
                    <div>                     
                        <Button loading={isSubmitting} 
                            disabled={isSubmitting}  onClick={() => console.log(errors)}
                            positive content='Submit' type='submit' fluid/> 
                            <Divider hidden={true}/>
                        <Button content='Back' negative onClick={() => modalStore.closeModal()} disabled={isSubmitting} fluid/>                                                    
                    </div>                                                           
                    <Divider hidden={true}/>
                    <Divider hidden={true}/>
                    <Divider hidden={true}/>
                </Form>
            )}
        </Formik>
    )
})